import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { EyeIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../contexts/ToastContext';
import proDocumentsService from '../../services/proDocuments.service';
import { formatUploadDate } from '../pro/components/docs/DocsTable';
import {
  bulkPrint, bulkZip, documentsSearchErrorMessage, searchDocuments,
} from '../../services/documentsSearch.service';
import { MAX_BULK_SELECTION, MAX_PRINT_SELECTION } from '../../types/documentsSearch';
import type {
  BulkItem, DocumentSearchFilters, DocumentSearchRow,
} from '../../types/documentsSearch';

/**
 * The parts of a "documents for these PROs" grid that are not layout.
 *
 * Two hosts share this: the Search Results Documents view (its own page-level grid,
 * with a filter bar and a checkbox selection) and a dashboard card repainted with
 * document results (one table in the card frame, whose action bar acts on the visible
 * rows). They differ in where the grid lives and where the acted-on set comes from —
 * everything else (columns, the fetch, the three bulk operations and their caps) is
 * identical, and lives here so neither host carries a second copy.
 *
 * The bulk operations in particular must not be duplicated: `runPrint` depends on a
 * popup-blocker dance that is easy to get subtly wrong.
 */

/** "3 file(s) couldn't be included: tif, xlsx" — types only when reported. */
export function skippedMessage(skipped: number, skippedTypes: string): string {
  const types = skippedTypes
    .split(',').map((t) => t.trim()).filter(Boolean).join(', ');
  return `${skipped} file(s) couldn't be included${types ? `: ${types}` : ''}.`;
}

/** The composite the bulk endpoints authorize on, projected off grid rows. */
export function rowsToBulkItems(rows: DocumentSearchRow[]): BulkItem[] {
  return rows.map((r) => ({
    source: r.source, ref: r.ref, fileinfo_recno: r.fileinfo_recno,
    pronumber: r.pronumber,
  }));
}

/**
 * The default request filters: active revisions, all types, no date bound.
 *
 * A module constant, not an inline literal, because useDocumentSearch keys its effect
 * on the filters object — a fresh `{}` per render would refetch forever.
 */
export const DEFAULT_DOCUMENT_FILTERS: DocumentSearchFilters = { revision: 'active' };

/** True when `filters` narrows anything, i.e. the filter bar is doing something. */
export function documentFiltersActive(filters: DocumentSearchFilters): boolean {
  return Boolean(
    filters.date_from || filters.date_to
    || (filters.documenttype && filters.documenttype.length > 0)
    || (filters.revision && filters.revision !== 'active'),
  );
}

/**
 * The document-type universe for a documents grid: a 3-branch union over cms_map +
 * cmsinvoice_map + legdocument_map. It needs the leg types (DOCUMENT PHOTO / DOCUMENT
 * SIGNATURE) and INVOICE that the PRO-only list lacks — hence a distinct scope AND a
 * distinct cache key, so this never shares DocsTab's narrower list (which feeds its
 * upload picker).
 */
export function useDocumentTypes() {
  return useQuery({
    queryKey: ['docTypes', 'search'],
    queryFn: () => proDocumentsService.documentTypes('search'),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

export interface DocumentSearchState {
  rows: DocumentSearchRow[];
  loading: boolean;
  error: string | null;
  /** The server hit its row ceiling; older documents are missing. */
  truncated: boolean;
  reload: () => void;
  /** Stable identity for the PRO set — hosts key selection resets on this. */
  prosKey: string;
}

/**
 * Every key a document row already owns — so no registry field of the same name is
 * ever offered as an added column.
 *
 * Not the same thing as the column set, and that is the point. `deleted` is a row
 * FIELD with no column of its own (it drives the struck-through styling, and the
 * revision filter), so the picker's column-id rules cannot see it — offer the order's
 * "Deleted" and the attached value lands on the document's own flag, striking out
 * every document whose ORDER is deleted. `billtorefno` is the other live clash: the
 * document row's is a GROUP_CONCAT of the PRO's invoice refs, the registry's is the
 * main invoice's one value.
 *
 * Derived from the row shape rather than listed as "these two", so a field added to
 * the registry tomorrow under a name this row uses is excluded without anyone
 * noticing it needed to be. Passed as the picker's Set form deliberately: these are
 * withheld, but no column of ours renders them, so none can take their title.
 * app.api...documents_search._merge_order_columns is the server-side backstop.
 */
export const DOCUMENT_ROW_KEYS: ReadonlySet<string> = new Set<keyof DocumentSearchRow>([
  'doc_key', 'source', 'ref', 'fileinfo_recno', 'pronumber', 'documenttype',
  'description', 'billtorefno', 'filename', 'filetype', 'uploadedby',
  'uploaddatetime', 'deleted', 'preview_url',
]);

/** Added order columns, and whether the saved selection is known yet. Both come from
 *  `useAdditionalFieldSelection`; a host that offers no added columns omits this. */
export interface DocumentSearchExtras {
  /** Orders-registry fieldnames to attach to each row, server-side. */
  additionalFields?: string[];
  /** False while the saved selection is still loading. The fetch WAITS on this —
   *  firing early runs the search twice, once without the added columns and once
   *  with, and the first answer paints a grid full of blank cells. */
  ready?: boolean;
}

/**
 * Load every document attached to `pros`.
 *
 * A stale in-flight response must not overwrite a newer one (filters change fast),
 * so a generation ref is checked before every setState. `prosKey` rather than the
 * array is the dependency: the array identity changes on every host render, its
 * CONTENT doesn't — and the added fields are keyed the same way, for the same reason.
 */
export function useDocumentSearch(
  pros: number[],
  filters: DocumentSearchFilters,
  { additionalFields, ready = true }: DocumentSearchExtras = {},
): DocumentSearchState {
  const [rows, setRows] = useState<DocumentSearchRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const reload = useCallback(() => setReloadNonce((n) => n + 1), []);

  const prosKey = useMemo(() => pros.join(','), [pros]);
  const fieldsKey = (additionalFields ?? []).join(',');

  const reqRef = useRef(0);
  useEffect(() => {
    // Hold everything — including the empty-PRO reset — until the saved column
    // selection is known, so the grid never paints a result the selection would
    // immediately invalidate.
    if (!ready) return;
    const gen = ++reqRef.current;
    const list = prosKey ? prosKey.split(',').map(Number) : [];
    if (list.length === 0) {
      setRows([]); setTruncated(false); setLoading(false); setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    searchDocuments(list, filters, fieldsKey ? fieldsKey.split(',') : [])
      .then((res) => {
        if (gen !== reqRef.current) return;
        setRows(res.rows);
        setTruncated(res.truncated);
      })
      .catch((err) => {
        if (gen !== reqRef.current) return;
        setRows([]);
        setTruncated(false);
        setError(documentsSearchErrorMessage(err, 'Failed to load documents.'));
      })
      .finally(() => {
        if (gen === reqRef.current) setLoading(false);
      });
  }, [prosKey, filters, reloadNonce, fieldsKey, ready]);

  return { rows, loading, error, truncated, reload, prosKey };
}

// ---------------------------------------------------------------------------
// Bulk operations
// ---------------------------------------------------------------------------

export interface DocumentBulkOps {
  busy: 'zip' | 'print' | null;
  runZip: (items: BulkItem[]) => Promise<void>;
  runPrint: (items: BulkItem[]) => void;
  /** True past the Email/ZIP ceiling the bulk endpoints validate. */
  overBulkCap: (count: number) => boolean;
  /** True past Print's own, stricter ceiling. */
  overPrintCap: (count: number) => boolean;
  /** ONE message covering whichever caps `count` exceeds, or null. */
  capMessage: (count: number) => string | null;
}

export function useDocumentBulkOps(): DocumentBulkOps {
  const { showToast } = useToast();
  const [busy, setBusy] = useState<'zip' | 'print' | null>(null);

  // Check-all can select every row the grid holds (up to the row ceiling) but the
  // bulk endpoints cap `items` — over that the server answers with a raw pydantic
  // validation error, so callers stop before sending.
  const overBulkCap = useCallback((count: number) => count > MAX_BULK_SELECTION, []);

  // Print's own, stricter cap: the server merges the whole selection into one PDF in
  // memory and 413s past the limit. Unlike the other caps that failure is expensive to
  // discover — the click has already claimed a browser tab and the user waits out the
  // whole merge before the error lands — so predict it and never send the request.
  const overPrintCap = useCallback((count: number) => count > MAX_PRINT_SELECTION, []);

  // Merged rather than stacked so the print line can't offer ZIP/Email as the way out
  // at a size where those are blocked too.
  const capMessage = useCallback((count: number) => {
    if (overBulkCap(count)) {
      return `${count.toLocaleString()} selected — Email and Download ZIP are limited `
        + `to ${MAX_BULK_SELECTION.toLocaleString()} documents, Print to `
        + `${MAX_PRINT_SELECTION.toLocaleString()}. Narrow your filters or select fewer.`;
    }
    if (overPrintCap(count)) {
      return `${count.toLocaleString()} selected — Print is limited to `
        + `${MAX_PRINT_SELECTION.toLocaleString()} documents. Email and Download ZIP `
        + 'are unaffected; select fewer to print.';
    }
    return null;
  }, [overBulkCap, overPrintCap]);

  const runZip = useCallback(async (items: BulkItem[]) => {
    setBusy('zip');
    try {
      const res = await bulkZip(items);
      if (res === null) return; // user cancelled the Save As dialog
      showToast({
        message: res.skipped > 0
          ? `ZIP downloaded. ${skippedMessage(res.skipped, res.skippedTypes)}`
          : `ZIP downloaded (${items.length} document${items.length === 1 ? '' : 's'}).`,
        type: res.skipped > 0 ? 'warning' : 'success',
      });
    } catch (err) {
      showToast({ message: documentsSearchErrorMessage(err, 'Download failed.'), type: 'error' });
    } finally {
      setBusy(null);
    }
  }, [showToast]);

  const runPrint = useCallback((items: BulkItem[]) => {
    // The merge takes seconds, and a popup blocker only trusts window.open inside the
    // click's own task — so claim the tab NOW and navigate it when the PDF resolves.
    // 'noopener' is deliberately omitted: it makes window.open return null, which
    // would defeat the whole pattern. The tab's back-reference is severed manually
    // instead; the target is a same-origin blob URL we built ourselves.
    let tab: Window | null = null;
    try {
      tab = window.open('', '_blank');
      if (tab) {
        tab.opener = null;
        tab.document.write(
          '<title>Preparing PDF…</title><p style="font-family:sans-serif">'
          + 'Merging the selected documents…</p>',
        );
        // Without close() the document stays in "loading" state forever and the tab
        // spins next to the placeholder text.
        tab.document.close();
      }
    } catch {
      tab = null;
    }
    setBusy('print');
    bulkPrint(items)
      .then((res) => {
        // A tab the user closed while the merge ran is still a non-null object, and
        // assigning location.href on it does nothing at all — so treat a closed
        // placeholder exactly like a blocked one and offer the link.
        if (tab && !tab.closed) {
          tab.location.href = res.url;
        } else {
          showToast({
            message: 'The print tab isn\'t available. Click here to open the PDF.',
            type: 'warning',
            persistent: true,
            onClick: () => window.open(res.url, '_blank'),
          });
        }
        if (res.skipped > 0) {
          showToast({
            message: skippedMessage(res.skipped, res.skippedTypes),
            type: 'warning',
          });
        }
      })
      .catch((err) => {
        tab?.close();
        showToast({ message: documentsSearchErrorMessage(err, 'Print failed.'), type: 'error' });
      })
      .finally(() => setBusy(null));
  }, [showToast]);

  return { busy, runZip, runPrint, overBulkCap, overPrintCap, capMessage };
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

export interface DocumentColumnDeps {
  /** Opens the PRO drawer. Renders the pro cell inert when omitted. */
  onProClick?: (pronumber: string) => void;
  onPreview: (row: DocumentSearchRow) => void;
  /**
   * Checkbox-selection wiring. Omit for a host whose bulk actions run against the
   * grid's visible rows instead of a selection — a dashboard card does, matching how
   * every other action in its action bar is scoped, so it gets no select column.
   */
  selection?: {
    isSelected: (docKey: string) => boolean;
    toggle: (row: DocumentSearchRow) => void;
    checkAll: (rows: DocumentSearchRow[]) => void;
  };
}

export function buildDocumentColumns(
  deps: DocumentColumnDeps,
): ColumnDef<DocumentSearchRow>[] {
  const { onProClick, onPreview, selection } = deps;
  const columns: ColumnDef<DocumentSearchRow>[] = [];

  if (selection) {
    const { isSelected, toggle, checkAll } = selection;
    columns.push({
      id: 'select',
      size: 40,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      // Check-all is scoped to the rows the grid is CURRENTLY showing (server
      // filters + TanTable's own column filters/search), not the whole fetch.
      header: ({ table }) => {
        // One pass, no intermediate array: this runs on EVERY render of a grid that
        // can hold 10,000 rows, so mapping them into a `shown` array here would
        // allocate 10,000 entries per render. The row array is built lazily inside
        // onChange, where it is actually needed (once, on click).
        const filtered = table.getFilteredRowModel().rows;
        let selectedShown = 0;
        for (const r of filtered) if (isSelected(r.original.doc_key)) selectedShown += 1;
        const all = filtered.length > 0 && selectedShown === filtered.length;
        const some = !all && selectedShown > 0;
        return (
          <input
            type="checkbox"
            aria-label="Select all documents"
            checked={all}
            ref={(el) => { if (el) el.indeterminate = some; }}
            onChange={(e) => {
              e.stopPropagation();
              checkAll(table.getFilteredRowModel().rows.map((r) => r.original));
            }}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
      cell: ({ row }) => (
        <input
          type="checkbox"
          // Visible values, not the internal doc_key — a screen reader user has no way
          // to tell which document "Select document 8134492_…_cms" is. Doc type is
          // included because one PRO's invoice and its POD can share a filename shape
          // and would otherwise sound identical.
          aria-label={
            `Select ${row.original.documenttype || 'document'} `
            + `${row.original.filename || ''} on PRO ${row.original.pronumber}`
          }
          checked={isSelected(row.original.doc_key)}
          onChange={(e) => { e.stopPropagation(); toggle(row.original); }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    });
  }

  columns.push(
    {
      id: 'pronumber',
      accessorKey: 'pronumber',
      header: 'Pro #',
      size: 110,
      enableSorting: true,
      enableColumnFilter: true,
      enableHiding: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onProClick?.(String(row.original.pronumber));
          }}
          disabled={!onProClick}
          className="font-semibold text-purple-700 hover:text-purple-900 hover:underline disabled:no-underline dark:text-purple-300 dark:hover:text-purple-200"
          title="Open order details"
        >
          {row.original.pronumber}
        </button>
      ),
    },
    {
      id: 'documenttype',
      accessorKey: 'documenttype',
      header: 'Doc Type',
      size: 180,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => <span className="truncate">{row.original.documenttype || ''}</span>,
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: 'Description',
      size: 240,
      enableSorting: true,
      enableColumnFilter: false,
      cell: ({ row }) => <span className="truncate">{row.original.description || ''}</span>,
    },
    {
      id: 'billtorefno',
      accessorKey: 'billtorefno',
      header: 'Bill To Ref #',
      // Every ref on the PRO's invoices, GROUP_CONCAT'd — where the registry's
      // `billtorefno` is the MAIN invoice's one value. Same name, different answer,
      // so this column must not be re-labelled from that definition if the registry
      // ever renames it. (The definition itself stays suppressed from the picker:
      // its generated column would land on this same id.)
      meta: { registryTitle: false },
      size: 160,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => <span className="truncate">{row.original.billtorefno || ''}</span>,
    },
    {
      id: 'uploadedby',
      accessorKey: 'uploadedby',
      header: 'Uploaded By',
      size: 130,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => <span>{row.original.uploadedby || ''}</span>,
    },
    {
      id: 'uploaddatetime',
      accessorKey: 'uploaddatetime',
      header: 'Upload Date',
      size: 150,
      enableSorting: true,
      enableColumnFilter: false,
      cell: ({ row }) => <span>{formatUploadDate(row.original.uploaddatetime)}</span>,
    },
    {
      id: 'preview',
      header: 'Preview',
      size: 80,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPreview(row.original); }}
          className="inline-flex items-center justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-blue-600 dark:hover:bg-gray-700"
          aria-label={`Preview ${row.original.filename || 'document'}`}
          title={row.original.filename || 'Preview document'}
        >
          <EyeIcon className="h-4 w-4" />
        </button>
      ),
    },
  );

  return columns;
}

/**
 * Both grids filter `pronumber` and `billtorefno` as TEXT rather than dropdowns:
 * with 10,000 documents loaded a dropdown filter would enumerate 10,000 distinct
 * options (siblings: AtRiskMonitor, SystemLogs).
 */
export const DOCUMENT_COLUMN_FILTER_TYPES = {
  pronumber: 'text' as const,
  billtorefno: 'text' as const,
};
